import StartupPageTitle from '../components/StartupPageTitle';
import { Brain as PageHeadingIcon } from '@phosphor-icons/react';
/**
 * BrandBrain.jsx — Module 04 (Sprint 6).
 *
 * Generate-with-Claude → edit inline → sync to active engagement brief.
 * One brand kit per founder; latest version is the "active" one.
 */
import React, { useEffect, useState } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import {
  Brain, Sparkle, ArrowsClockwise, FloppyDisk, ArrowClockwise,
  Trash, Plus, MagicWand, Megaphone, Quotes, X,
} from '@phosphor-icons/react';

const ACCENT = '#10b981';

const emptyKit = {
  brand_description: '', mission: '', vision: '',
  values: [],
  tone_axes: [
    { label: 'Formality', left: 'Casual', right: 'Formal', position: 50 },
    { label: 'Mood', left: 'Playful', right: 'Serious', position: 50 },
    { label: 'Energy', left: 'Calm', right: 'Energetic', position: 50 },
  ],
  do_words: [], dont_words: [], sample_taglines: [],
  sample_social_posts: [],
};

const BrandBrain = () => {
  const [active, setActive] = useState(null);
  const [kit, setKit] = useState(emptyKit);
  const [loading, setLoading] = useState(true);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/brand-brain');
        if (!cancelled) {
          setActive(r.data.active);
          if (r.data.active?.kit) setKit({ ...emptyKit, ...r.data.active.kit });
        }
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || 'Failed to load');
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const patch = (k, v) => { setKit((p) => ({ ...p, [k]: v })); setDirty(true); };
  const updateAxis = (idx, k, v) => { setKit((p) => ({ ...p, tone_axes: p.tone_axes.map((a, i) => i === idx ? { ...a, [k]: v } : a) })); setDirty(true); };
  const addArrayItem = (k) => patch(k, [...(kit[k] || []), '']);
  const updateArrayItem = (k, idx, v) => patch(k, kit[k].map((x, i) => i === idx ? v : x));
  const removeArrayItem = (k, idx) => patch(k, kit[k].filter((_, i) => i !== idx));

  const save = async (sync = true) => {
    setBusy(true);
    try {
      const r = await api.put('/brand-brain', { kit, sync_to_brief: sync });
      setActive(r.data.active);
      setDirty(false);
      toast.success(sync && r.data.sync?.synced ? 'Saved & synced to active engagement brief' : 'Saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="p-12 text-sm text-text-muted inline-flex items-center gap-2"><ArrowsClockwise size={14} className="animate-spin" /> Loading…</div>;

  // Empty state
  if (!active && !generateOpen) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto" data-testid="brand-brain-empty">
        <div className="text-center py-16">
          <div className="w-16 h-16 rounded-2xl inline-flex items-center justify-center mb-4" style={{ background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT}aa)` }}>
            <Brain size={28} weight="fill" className="text-white" />
          </div>
          <StartupPageTitle icon={PageHeadingIcon} className="text-3xl sm:text-4xl font-heading font-bold text-text-primary mb-3" style={{ letterSpacing: '-0.02em' }}>Your Brand Brain</StartupPageTitle>
          <p className="text-sm text-text-secondary max-w-md mx-auto mb-6">
            Generate a structured brand kit &mdash; description, mission, values, tone-of-voice axes, do/don&apos;t words, taglines, and ready-to-post copy &mdash; powered by Claude.
          </p>
          <button onClick={() => setGenerateOpen(true)} data-testid="brand-brain-generate-cta" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold text-white" style={{ backgroundColor: ACCENT }}>
            <Sparkle size={14} weight="fill" /> Generate brand kit with AI
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1080px] mx-auto pb-24" data-testid="brand-brain">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6 startup-page-header">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-2" style={{ color: ACCENT }}>Module 04</div>
          <StartupPageTitle icon={PageHeadingIcon} className="text-3xl sm:text-4xl font-heading font-bold text-text-primary leading-[1.05] mb-1" style={{ letterSpacing: '-0.02em' }}>
            {active?.brand_name || 'Brand Brain'}
          </StartupPageTitle>
          {active?.one_liner && <p className="text-sm text-text-secondary">{active.one_liner}</p>}
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setGenerateOpen(true)} data-testid="brand-brain-regenerate" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border border-border hover:border-text-primary">
            <ArrowClockwise size={12} weight="bold" /> Regenerate
          </button>
          <button onClick={() => save(true)} disabled={busy || !dirty} data-testid="brand-brain-save" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50" style={{ backgroundColor: ACCENT }}>
            <FloppyDisk size={12} weight="bold" /> Save & sync brief
          </button>
        </div>
      </div>

      {dirty && (
        <div className="bg-warning/10 border border-warning/30 text-warning rounded-lg px-3 py-2 mb-5 text-xs inline-flex items-center gap-2">
          <Sparkle size={12} weight="bold" /> You have unsaved edits. Click &ldquo;Save &amp; sync brief&rdquo; to push these into your active engagement.
        </div>
      )}

      <Section title="Brand description">
        <textarea value={kit.brand_description} onChange={(e) => patch('brand_description', e.target.value)} rows={3} data-testid="bb-description" className="w-full bg-surface border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-text-primary leading-relaxed" />
      </Section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Section title="Mission">
          <textarea value={kit.mission} onChange={(e) => patch('mission', e.target.value)} rows={3} data-testid="bb-mission" className="w-full bg-surface border border-border rounded-lg px-3 py-2.5 text-sm" />
        </Section>
        <Section title="Vision">
          <textarea value={kit.vision} onChange={(e) => patch('vision', e.target.value)} rows={3} data-testid="bb-vision" className="w-full bg-surface border border-border rounded-lg px-3 py-2.5 text-sm" />
        </Section>
      </div>

      <Section title="Values">
        <ArrayEditor items={kit.values || []} onAdd={() => addArrayItem('values')} onUpdate={(i, v) => updateArrayItem('values', i, v)} onRemove={(i) => removeArrayItem('values', i)} placeholder="e.g. Relentless reliability" testidPrefix="bb-value" />
      </Section>

      <Section title="Tone of voice">
        <div className="space-y-4" data-testid="bb-tone-axes">
          {(kit.tone_axes || []).map((axis, idx) => (
            <div key={idx} className="bg-surface border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3 gap-2">
                <input value={axis.label} onChange={(e) => updateAxis(idx, 'label', e.target.value)} className="text-sm font-semibold bg-transparent border-b border-transparent hover:border-border focus:border-text-primary outline-none flex-1" />
              </div>
              <div className="flex items-center gap-3">
                <input value={axis.left}  onChange={(e) => updateAxis(idx, 'left', e.target.value)} className="text-xs bg-background border border-border rounded px-2 py-1 w-28" placeholder="left label" />
                <div className="flex-1 relative">
                  <input type="range" min="0" max="100" value={axis.position} onChange={(e) => updateAxis(idx, 'position', Number(e.target.value))} data-testid={`bb-tone-slider-${idx}`} className="w-full accent-[#10b981]" />
                  <div className="text-center text-[10px] text-text-muted font-mono mt-1">{axis.position}/100</div>
                </div>
                <input value={axis.right} onChange={(e) => updateAxis(idx, 'right', e.target.value)} className="text-xs bg-background border border-border rounded px-2 py-1 w-28 text-right" placeholder="right label" />
              </div>
            </div>
          ))}
        </div>
      </Section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Section title="Do say">
          <ArrayEditor items={kit.do_words || []} onAdd={() => addArrayItem('do_words')} onUpdate={(i, v) => updateArrayItem('do_words', i, v)} onRemove={(i) => removeArrayItem('do_words', i)} placeholder="e.g. intelligent" testidPrefix="bb-doword" compact />
        </Section>
        <Section title="Don&apos;t say">
          <ArrayEditor items={kit.dont_words || []} onAdd={() => addArrayItem('dont_words')} onUpdate={(i, v) => updateArrayItem('dont_words', i, v)} onRemove={(i) => removeArrayItem('dont_words', i)} placeholder="e.g. revolutionary" testidPrefix="bb-dontword" compact />
        </Section>
      </div>

      <Section title="Sample taglines">
        <ArrayEditor items={kit.sample_taglines || []} onAdd={() => addArrayItem('sample_taglines')} onUpdate={(i, v) => updateArrayItem('sample_taglines', i, v)} onRemove={(i) => removeArrayItem('sample_taglines', i)} placeholder="e.g. Your kitchen, simplified." testidPrefix="bb-tagline" />
      </Section>

      <Section title="Sample social posts">
        <div className="space-y-3" data-testid="bb-social-posts">
          {(kit.sample_social_posts || []).map((post, idx) => (
            <div key={idx} className="bg-surface border border-border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <input value={post.platform} onChange={(e) => patch('sample_social_posts', kit.sample_social_posts.map((p, i) => i === idx ? { ...p, platform: e.target.value } : p))} className="text-[10px] uppercase tracking-wider font-bold text-text-muted bg-transparent outline-none border-b border-transparent hover:border-border w-32" />
                <button onClick={() => patch('sample_social_posts', kit.sample_social_posts.filter((_, i) => i !== idx))} className="text-text-muted hover:text-danger"><Trash size={11} weight="bold" /></button>
              </div>
              <textarea value={post.copy} onChange={(e) => patch('sample_social_posts', kit.sample_social_posts.map((p, i) => i === idx ? { ...p, copy: e.target.value } : p))} rows={3} className="w-full bg-background border border-border rounded px-2 py-1.5 text-sm focus:outline-none focus:border-text-primary leading-relaxed" />
            </div>
          ))}
          <button onClick={() => patch('sample_social_posts', [...(kit.sample_social_posts || []), { platform: 'LinkedIn', copy: '' }])} className="text-xs text-text-secondary hover:text-text-primary inline-flex items-center gap-1"><Plus size={11} weight="bold" /> Add post</button>
        </div>
      </Section>

      {generateOpen && <GenerateModal initial={{ brand_name: active?.brand_name || '', one_liner: active?.one_liner || '', sector: active?.sector || '', target_audience: active?.target_audience || '' }} onClose={() => setGenerateOpen(false)} onGenerated={(r) => {
        setActive(r.active);
        if (r.active?.kit) setKit({ ...emptyKit, ...r.active.kit });
        setDirty(false);
        setGenerateOpen(false);
      }} />}
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="mb-6">
    <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-text-muted mb-2">{title}</div>
    {children}
  </div>
);

const ArrayEditor = ({ items, onAdd, onUpdate, onRemove, placeholder, testidPrefix, compact }) => (
  <div className={compact ? 'flex flex-wrap gap-1.5' : 'space-y-1.5'}>
    {items.map((item, i) => compact ? (
      <span key={i} className="bg-surface border border-border rounded-full px-2.5 py-1 inline-flex items-center gap-1 text-xs">
        <input value={item} onChange={(e) => onUpdate(i, e.target.value)} data-testid={`${testidPrefix}-${i}`} className="bg-transparent outline-none w-20 text-text-primary" placeholder={placeholder} />
        <button onClick={() => onRemove(i)} className="text-text-muted hover:text-danger"><X size={10} weight="bold" /></button>
      </span>
    ) : (
      <div key={i} className="flex items-center gap-2 bg-surface border border-border rounded-lg px-3 py-1.5">
        <input value={item} onChange={(e) => onUpdate(i, e.target.value)} data-testid={`${testidPrefix}-${i}`} className="flex-1 bg-transparent outline-none text-sm text-text-primary" placeholder={placeholder} />
        <button onClick={() => onRemove(i)} className="text-text-muted hover:text-danger"><Trash size={11} weight="bold" /></button>
      </div>
    ))}
    <button onClick={onAdd} className="text-xs text-text-secondary hover:text-text-primary inline-flex items-center gap-1 mt-1" data-testid={`${testidPrefix}-add`}>
      <Plus size={11} weight="bold" /> Add
    </button>
  </div>
);

const GenerateModal = ({ initial, onClose, onGenerated }) => {
  const [form, setForm] = useState({
    brand_name: initial.brand_name,
    one_liner: initial.one_liner,
    sector: initial.sector,
    target_audience: initial.target_audience,
    extra_notes: '',
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.brand_name.trim() || !form.one_liner.trim()) { toast.error('Brand name + one-liner are required'); return; }
    setBusy(true);
    try {
      const r = await api.post('/brand-brain/generate', form, { timeout: 90000 });
      toast.success('Brand kit generated!');
      onGenerated(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed — try again');
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur z-50 flex items-start justify-center pt-16 p-4 overflow-y-auto" onClick={onClose} data-testid="brand-brain-generate-modal">
      <div className="bg-surface border border-border rounded-xl p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-heading font-bold text-text-primary text-lg inline-flex items-center gap-2" style={{ letterSpacing: '-0.01em' }}>
              <Sparkle size={15} weight="fill" style={{ color: ACCENT }} /> Generate brand kit
            </h3>
            <p className="text-xs text-text-muted mt-0.5">Powered by Claude. Takes ~20-30 seconds.</p>
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary"><X size={16} weight="bold" /></button>
        </div>
        <div className="space-y-3">
          <Field label="Brand name *"           value={form.brand_name}      onChange={(v) => setForm((p) => ({ ...p, brand_name: v }))} testid="bb-gen-name" />
          <Field label="One-liner *"            value={form.one_liner}       onChange={(v) => setForm((p) => ({ ...p, one_liner: v }))} placeholder="What do you do, in one sentence?" testid="bb-gen-oneliner" />
          <Field label="Sector"                 value={form.sector}          onChange={(v) => setForm((p) => ({ ...p, sector: v }))} placeholder="e.g. B2B SaaS, fintech, climate" testid="bb-gen-sector" />
          <Field label="Target audience"        value={form.target_audience} onChange={(v) => setForm((p) => ({ ...p, target_audience: v }))} placeholder="e.g. CTOs at mid-market SaaS in APAC" testid="bb-gen-audience" />
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Additional notes</label>
            <textarea value={form.extra_notes} onChange={(e) => setForm((p) => ({ ...p, extra_notes: e.target.value }))} rows={3} placeholder="Anything that should shape the voice — competitors to differentiate from, key vocabulary, taboo topics, etc." data-testid="bb-gen-notes" className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm" />
          </div>
        </div>
        <div className="flex gap-3 mt-5">
          <button onClick={onClose} disabled={busy} className="flex-1 px-4 py-2.5 text-sm border border-border rounded-lg disabled:opacity-50">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="bb-gen-submit" className="flex-1 px-4 py-2.5 text-sm font-semibold text-white rounded-lg disabled:opacity-50 inline-flex items-center justify-center gap-2" style={{ backgroundColor: ACCENT }}>
            {busy ? <><ArrowsClockwise size={12} className="animate-spin" /> Generating…</> : <><MagicWand size={12} weight="bold" /> Generate</>}
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text', placeholder, testid }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-text-primary" />
  </div>
);

export default BrandBrain;
